import { StandardButton } from '@/components/buttons';
import { Button, Flex, Image, Loader, Text, Title } from '@mantine/core';
import { IconArrowLeft, IconCheck } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'motion/react';
import { useBaleOutboundContext } from '../hook';
import styles from '../styles.module.css';

// ============================================================================
// TYPES
// ============================================================================

interface ScannedBale {
	name: string;
	value: string;
	emoji: string;
	count: number;
	codes: string[];
}

interface FormData {
	docketId: string | null;
	docketPhoto: string | null;
	weight: number | null;
	truckRegistration: string | null;
	photos: string[];
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Summary section header with title and check icon
 */
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
	<Flex align="center" gap="xs" mb="xs">
		<Title order={3} fw={600}>
			{title}
		</Title>
		<IconCheck size={16} color="var(--mantine-color-green-6)" />
	</Flex>
);

/**
 * Key-value display row for form details
 */
const DetailRow: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
	<Flex align="center" justify="space-between" py="xs">
		<Text size="sm" fw={500}>
			{label}
		</Text>
		<Text size="md" fw={600} c="black">
			{value}
		</Text>
	</Flex>
);

/**
 * Scanned bales summary section showing material types and counts
 */
const ScannedBalesSection: React.FC<{ scannedBales: ScannedBale[] }> = ({ scannedBales }) => {
	const scannedMaterials = scannedBales.filter((bale) => bale.count > 0);
	const totalScannedBales = scannedMaterials.reduce((sum, bale) => sum + bale.count, 0);

	return (
		<Flex direction="column" w="100%" className={styles.sectionMarginTop}>
			<SectionHeader
				title={`Scanned Bales (${totalScannedBales} ${totalScannedBales === 1 ? 'bale' : 'bales'})`}
			/>
			<Flex direction="column" gap="xs">
				{scannedMaterials.map((bale) => (
					<Flex key={bale.value} align="center" justify="space-between" py="xs">
						<Flex align="center" gap="sm">
							<Text size="xl">{bale.emoji}</Text>
							<Text size="md" fw={500}>
								{bale.name}
							</Text>
						</Flex>
						<Text size="md" fw={600} c="black">
							{bale.count} {bale.count === 1 ? 'bale' : 'bales'}
						</Text>
					</Flex>
				))}
			</Flex>
		</Flex>
	);
};

/**
 * Load details summary section showing weight, truck registration, and customer
 */
const LoadDetailsSection: React.FC<{ formData: FormData }> = ({ formData }) => (
	<Flex direction="column" w="100%" className={styles.sectionMarginTop}>
		<SectionHeader title="Load Details" />
		<Flex direction="column" gap="xs">
			<DetailRow label="Load weight" value={`${formData.weight} Tonne`} />
			<DetailRow label="Truck registration" value={formData.truckRegistration || ''} />
		</Flex>
	</Flex>
);

/**
 * Docket summary section showing docket ID and photo
 */
const DocketSection: React.FC<{ formData: FormData }> = ({ formData }) => (
	<Flex direction="column" w="100%" className={styles.sectionMarginTop}>
		<SectionHeader title="Docket" />
		<Flex direction="column" gap="xs">
			<DetailRow label="Docket ID" value={formData.docketId || ''} />
			{formData.docketPhoto && (
				<Flex direction="column" gap="xs" mt="xs">
					<Text size="sm" fw={500}>
						Docket photo
					</Text>
					<Flex className={styles.docketPhotoStyle}>
						<Image src={formData.docketPhoto} alt="Docket Photo" fit="cover" w="100%" h="100%" />
					</Flex>
				</Flex>
			)}
		</Flex>
	</Flex>
);

/**
 * Load photos summary section showing all uploaded photos
 */
const LoadPhotosSection: React.FC<{ photos: string[] }> = ({ photos }) => (
	<Flex direction="column" w="100%" className={styles.sectionMarginTop}>
		<SectionHeader title="Load Photos" />
		<Flex direction="column" gap="xs">
			<Text size="sm" fw={500}>
				{photos.length} {photos.length === 1 ? 'photo' : 'photos'}
			</Text>
			<Flex className={styles.horizontalScrollContainer}>
				{photos.map((photo, i) => (
					<Flex key={i} className={styles.photoCard}>
						<Image src={photo} alt={`Load Photo ${i + 1}`} fit="cover" />
					</Flex>
				))}
			</Flex>
		</Flex>
	</Flex>
);

/**
 * Loading overlay with smooth animations for submission process
 */
const SubmissionLoadingOverlay: React.FC<{ isVisible: boolean }> = ({ isVisible }) => (
	<AnimatePresence>
		{isVisible && (
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.3, ease: 'easeInOut' }}
				className={styles.loadingOverlay}
			>
				<Flex h="100%" w="100%" align="center" justify="center" direction="column" gap="md">
					<motion.div
						initial={{ opacity: 0, scale: 0.8 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.8 }}
						transition={{
							duration: 0.4,
							delay: 0.1,
							ease: 'easeOut',
						}}
					>
						<Loader color="blue" size="lg" />
					</motion.div>

					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{
							duration: 0.3,
							delay: 0.2,
							ease: 'easeOut',
						}}
					>
						<Text size="md" ta="center" c="white" fw={500}>
							Submitting load...
						</Text>
					</motion.div>
				</Flex>
			</motion.div>
		)}
	</AnimatePresence>
);

/**
 * Action buttons for navigation and submission
 */
const ActionButtons: React.FC<{
	onBack: () => void;
	onSubmit: () => Promise<void>;
	isLoading: boolean;
}> = ({ onBack, onSubmit, isLoading }) => (
	<Flex
		className={`${styles.outboundBottomPanel} ${styles.confirmActionButtons}`}
		align="center"
		justify="center"
		gap="md"
		px="md"
	>
		<Button
			variant="outline"
			fullWidth
			onClick={onBack}
			disabled={isLoading}
			className={styles.confirmActionButtonSecondary}
		>
			Back to Edit
		</Button>
		<StandardButton
			variant="filled"
			fullWidth
			onClick={onSubmit}
			loading={isLoading}
			className={styles.confirmActionButtonPrimary}
		>
			Submit Load
		</StandardButton>
	</Flex>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Confirmation page for reviewing and submitting the bale outbound load.
 *
 * This page displays a comprehensive summary of:
 * - Scanned bales by material type
 * - Load details (weight, truck registration, customer)
 * - Docket information and photo
 * - Load photos
 *
 * Provides final submission with loading state and animation.
 */
export const BaleOutboundConfirmSubmissionPage = () => {
	// ========================================================================
	// CONTEXT AND STATE
	// ========================================================================

	const { setPage, formData, scannedBales, handleCreateOutboundBale, loadingSubmission } =
		useBaleOutboundContext();

	// ========================================================================
	// EVENT HANDLERS
	// ========================================================================

	/**
	 * Handle back navigation to root page
	 */
	const handleBack = () => setPage('ROOT');

	/**
	 * Handle form submission
	 */
	const handleSubmit = async () => {
		await handleCreateOutboundBale();
	};

	// ========================================================================
	// RENDER
	// ========================================================================

	return (
		<Flex className={styles.outboundMainContainer}>
			{/* Header */}
			<Flex
				className={styles.outboundHeader}
				align="center"
				justify="space-between"
				gap="sm"
				style={{ paddingBottom: '8px' }}
			>
				<Flex align="center" gap="sm">
					<IconArrowLeft onClick={handleBack} style={{ cursor: 'pointer' }} />
					<Title order={2}>Review Submission</Title>
				</Flex>
			</Flex>

			{/* Scrollable Content */}
			<Flex
				className={`${styles.outboundScrollableContent} ${styles.confirmContentPadding}`}
				px="md"
			>
				<ScannedBalesSection scannedBales={scannedBales} />
				<LoadDetailsSection formData={formData} />
				<DocketSection formData={formData} />
				<LoadPhotosSection photos={formData.photos} />
			</Flex>

			{/* Action Buttons */}
			<ActionButtons onBack={handleBack} onSubmit={handleSubmit} isLoading={loadingSubmission} />

			{/* Loading Overlay */}
			<SubmissionLoadingOverlay isVisible={loadingSubmission} />
		</Flex>
	);
};
